import { t } from 'i18next'
import * as R from 'ramda'
import { getUUID } from '../../../common/common-utils'
import {
  AssessmentEquipmentGroupAnswers,
  Building,
  BuildingDetails,
  BuildingStId,
  Contact,
  ContractRole,
  Criticality,
  Customer,
  Elevator,
  Equipment,
  EquipmentGroup,
  EquipmentGroups,
  Escalator,
  ExtraService,
  Offer,
  Opportunity,
  OpportunityBuilding,
  OpportunityElevator,
  OpportunityEquipment,
  OpportunityEscalator,
  Partner,
  GroupKind,
  ClientFrontlineConfig,
  PartnerCustomDetails
} from '../../../../types/types'
import { updateRecommendedOffer } from '../offering/recommendation'
import { getEquipmentCountChanges } from '../escalation/escalation-offer-utils'
import { Offering } from '../offering/types'
import { fromDomain, toDomain } from '../offering/dto'
import { getContractPartnersRole } from '../../../common/partner-utils'
import { isDoorsOpportunity } from '../../../common/opportunity'
import { setOfferModified } from '../../utils/salesforce-sync-utils'
import { EquipmentCategory } from 'nemo-pricing'
import { DxGroup } from 'app/app/common/temp/frontline-config'
import { getDefaultCommercialTerms, resetCommercialTerms } from '../../utils/commercialterms/CommercialTermsUtils'
const checkIfDx = (_eq: OpportunityEquipment): boolean => true
const isVasOpportunity = (_opportunityCategory: string): boolean => true

export function groupKindForCategory(equipmentCategory: EquipmentCategory): GroupKind {
  switch (equipmentCategory) {
    case 'door':
      return 'doors'
    case 'elevator':
    case 'escalator':
      return 'elevators_and_escalators'
    case 'resiflow':
      return 'people_flow'
  }
}
/**
 * Merge data from Salesforce opportunity to the offer.
 *
 * New equipment, partners and contacts will be added to the offer.
 * Existing details will be updated so that new salesforce data will overwrite all local changes.
 * Removed salesforce entities will be removed from the offer as well. One exception is
 * that if building originally came from salesforce, it won't be deleted even
 * if the attached equipment were deleted if the building also contains locally added equipment.
 * Opportunity might be assigned to a different salesforce account
 * ContractRoles need to be updated if partner was added/deleted in the update.
 */
export const updateOfferSalesforceData =
  (opportunity: Opportunity, customer: Customer, frontlineConfig: ClientFrontlineConfig) =>
  (offer: Offer): Offer => {
    try {
      // const allBundles = frontlineConfig.elevatorBundles
      // const bundleKeys = allBundles.map(bundle => bundle.key)
      // const appliedBundles = getAppliedBundles(offer, bundleKeys)
      // const isBundleApplied = !R.isEmpty(appliedBundles)
      const offering: Offering = toDomain(offer)
      // const groupQuestions = getGroupOfferingQuestionsByOffering(
      //   frontlineConfig.offeringQuestions,
      //   offering,
      //   offer.isGSMOffer // TODO: Remove as part of GSM Clean up
      // )
      // const allStatesByGroup = initGroupStates(
      //   groupQuestions,
      //   initGroupValidationStates(frontlineConfig.offeringQuestions, offering, offer)
      // )
      // if (isBundleApplied) {
      //   appliedBundles.forEach(bundle => {
      //     const { bundleGroupSelection } = offer
      //     const { groupId, equipmentCategory, key } = bundle
      //     const bundleSelection: BundleSelection = R.pathOr(
      //       {} as BundleSelection,
      //       [groupId, equipmentCategory, key],
      //       bundleGroupSelection
      //     )
      //     if (!R.isEmpty(bundleSelection)) {
      //       const { serviceList, isActive, version, type } = bundleSelection
      //       const bundleObj = {
      //         key,
      //         category: equipmentCategory,
      //         serviceList,
      //         isActive,
      //         version,
      //         type,
      //         is_vas_addendum: false,
      //         is_gsm_campaign: false
      //       }
      //       const path = { groupId, criticality: 'normal' as Criticality, equipmentCategory, bundle: bundleObj }
      //       offering = getUpdatedOffering(frontlineConfig, offering, offer, allStatesByGroup, path, isBundleApplied)
      //     }
      //   })
      // }
      let newOffer = fromDomain(offer, offering)
      const isDoorOrVas =
        isDoorsOpportunity(newOffer.opportunity) || isVasOpportunity(newOffer.opportunity.opportunityCategory)
      const isDXGrpAvailable =
        !isDoorOrVas &&
        Object.keys(newOffer.equipmentGroups).length > 0 &&
        Object.values(newOffer.equipmentGroups).find(eg => newOffer.dxGrpId && eg.salesToolId === newOffer.dxGrpId)
      const dxGrpId = isDXGrpAvailable && isDXGrpAvailable !== undefined ? isDXGrpAvailable.salesToolId : ''
      const defaultGroupsByKind = getDefaultGroups(opportunity, newOffer)
      const isOpportunityCategoryChanged = opportunity.opportunityCategory !== offer.opportunity.opportunityCategory
      const buildings = isOpportunityCategoryChanged
        ? resetBuildingsAfterOpportunityCategoryChange(
            opportunity.buildings,
            newOffer.assessmentMethod !== 'standard',
            defaultGroupsByKind,
            dxGrpId || '',
            isDoorOrVas,
            opportunity.id
          )
        : mergeBuildings(
            newOffer.buildings,
            opportunity.buildings,
            newOffer.assessmentMethod !== 'standard',
            defaultGroupsByKind,
            dxGrpId || '',
            isDoorOrVas,
            opportunity.id
          )

      const contacts = updateContacts(newOffer.contacts, opportunity, customer)
      const partner = opportunity.partner
      const partners = opportunity.partners
      const partnerFreeText = updatePartnerFreeText(newOffer.partner, opportunity.partner, newOffer.partnerFreeText)
      const partnerFreeTextList: PartnerCustomDetails[] | null = partnerFreeText
        ? (newOffer.partner && [{ accountNumber: newOffer.partner.accountNumber, freeText: partnerFreeText }]) || null
        : null
      const contractRoles = updateContractRoles(newOffer, opportunity)

      const assessmentEquipmentGroupAnswers = isOpportunityCategoryChanged
        ? {}
        : updateAssessmentEquipmentGroupAnswers(newOffer.assessmentEquipmentGroupAnswers, buildings)

      const getDXFromGlobalAvailable = DxGroup.get()
      const dxGrpFromGlobal =
        !isDoorOrVas && getDXFromGlobalAvailable !== undefined ? getDXFromGlobalAvailable[opportunity.id] : false
      let defaultGroups = defaultGroupsByKind
      if (!isDoorOrVas && !isDXGrpAvailable && dxGrpFromGlobal) {
        if (
          Object.keys(defaultGroups).length > 0 &&
          !Object.keys(newOffer.equipmentGroups).includes(defaultGroups.elevators_and_escalators.salesToolId || '')
        ) {
          newOffer = {
            ...newOffer,
            equipmentGroups: {
              ...newOffer.equipmentGroups,
              [defaultGroupsByKind.elevators_and_escalators.salesToolId || '']:
                defaultGroupsByKind.elevators_and_escalators
            }
          }
        }
        defaultGroups = {
          ...defaultGroups,
          elevators_and_escalators: dxGrpFromGlobal
        }
      }

      const equipmentGroups = updateEquipmentGroups(newOffer, buildings, defaultGroups, isOpportunityCategoryChanged)
      const extraServices = updateExtraServices(newOffer, equipmentGroups)
      const equipmentGroupsUnchanged = R.equals(R.keys(equipmentGroups), R.keys(newOffer.equipmentGroups))
      const isGSMOffer = offer.isGSMOffer || null
      const selectedInteractionRating =
        opportunity && isOpportunityCategoryChanged ? null : offer.selectedInteractionRating
      const isBillingPlanAtEquipmentLevel =
        opportunity && isOpportunityCategoryChanged && offer.opportunity.opportunityCategory === 'Renegotiation'
          ? false
          : offer.isBillingPlanAtEquipmentLevel
      const isContractStartDateAtEquipmentLevel =
        opportunity && isOpportunityCategoryChanged && offer.opportunity.opportunityCategory === 'Renegotiation'
          ? false
          : offer.isContractStartDateAtEquipmentLevel
      const commercialTerms =
        opportunity && isOpportunityCategoryChanged
          ? getDefaultCommercialTerms(frontlineConfig, newOffer.opportunity)
          : resetCommercialTerms(frontlineConfig, newOffer.commercialTerms, opportunity, true)

      if (opportunity && isOpportunityCategoryChanged && offer.opportunity.opportunityCategory === 'Renegotiation') {
        delete commercialTerms.AUGRU
      }
      const updatedOffer = {
        ...setOfferModified(newOffer),
        opportunity,
        customer,
        buildings,
        contacts,
        partner,
        partners,
        contractRoles,
        assessmentEquipmentGroupAnswers,
        isGSMOffer,
        partnerFreeText,
        partnerFreeTextList,
        equipmentGroups,
        extraServices,
        bundleGroupSelection: {},
        commercialTerms,
        isBillingPlanAtEquipmentLevel,
        isContractStartDateAtEquipmentLevel,
        selectedInteractionRating
      }
      const {
        equipmentsInOpportunity,
        equipmentsInOffer,
        isEquipmentEnabledInOffer,
        contractEquipmentsTotalAnnualValue
      } = getEquipmentCountChanges(opportunity, buildings, updatedOffer, frontlineConfig)

      const modifiedOffer = {
        ...updatedOffer,
        equipmentsInOffer,
        equipmentsInOpportunity,
        isEquipmentEnabledInOffer,
        contractEquipmentsTotalAnnualValue
      }

      const modifiedOfferWithDxGrpId = modifiedOffer
      // TODO fix this commented code while implementing DX group
      // !isDXGrpAvailable && dxGrpFromGlobal
      //   ? {
      //       ...modifiedOffer,
      //       dxGrpId: dxGrpFromGlobal.salesToolId
      //     }
      //  : modifiedOffer

      return equipmentGroupsUnchanged
        ? modifiedOfferWithDxGrpId
        : updateRecommendedOffer(modifiedOfferWithDxGrpId, frontlineConfig)
    } catch (e) {
      console.log(e)
      // handleError(fromError(e as any))
    }
    return offer
  }

// function getUpdatedOffering(
//   frontlineConfig: ClientFrontlineConfig,
//   offering: Offering,
//   offer: Offer,
//   allStatesByGroup: GroupStates,
//   path: BundlePath,
//   isBundleApplied: boolean
// ) {
//   switch (offer.quickTenderScope) {
//     case 'flexible':
//     case 'value_added_services_addendum':
//     case 'gsm_campaign': // TODO: Remove as part of GSM Clean up
//       return getUpdateOffering(frontlineConfig, offering, offer, allStatesByGroup, path, isBundleApplied, [])
//         .updatedOffering
//     default:
//       return offering
//   }
// }

const getOfferEquipmentCategories = (buildings: Building[]) =>
  R.chain(b => mapEquipmentCategory(b.details.equipment), buildings)
const getOpportunityEquipmentCategories = (buildings: OpportunityBuilding[]) =>
  R.chain(b => mapEquipmentCategory(b.equipment), buildings)
const mapEquipmentCategory = (equipment: Array<OpportunityEquipment | Equipment>): EquipmentCategory[] =>
  R.map(e => e.equipmentCategory, equipment)

function getDefaultGroups(opportunity: Opportunity, offer: Offer): Record<GroupKind, EquipmentGroup> {
  const { buildings } = opportunity
  const { buildings: offerBuildings } = offer

  const equipmentGroupNameTranslation = t('equipment.group.default')
  const equipmentGroupName =
    equipmentGroupNameTranslation != null ? equipmentGroupNameTranslation : 'equipment.group.default'

  const equipmentCategories = R.uniq([
    ...getOfferEquipmentCategories(offerBuildings),
    ...getOpportunityEquipmentCategories(buildings)
  ])

  return equipmentCategories.reduce(
    (acc, category) => {
      const groupKind = groupKindForCategory(category)
      const possibleDefaultGroupNondx =
        Object.keys(offer.equipmentGroups).length > 0 &&
        (R.values(offer.equipmentGroups) as EquipmentGroup[]).find(
          eg => eg.groupKind === groupKind && eg.salesToolId !== offer.dxGrpId
        )
      if (possibleDefaultGroupNondx) {
        return R.assoc(groupKind, possibleDefaultGroupNondx, acc)
      } else {
        // no groups available, so create one
        return R.assoc(
          groupKind,
          {
            salesToolId: getUUID(),
            name:
              groupKind === 'elevators_and_escalators' && !isVasOpportunity(opportunity.opportunityCategory)
                ? t('equipment.group.nonDxGroup.name')
                : equipmentGroupName,
            groupKind
          },
          acc
        )
      }
    },
    {} as Record<GroupKind, EquipmentGroup>
  )
}

function mergeBuildings(
  offerBuildings: Building[],
  opportunityBuildings: OpportunityBuilding[],
  isGroupedAssessment: boolean,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  dxGrpId: string,
  isDoorOrVas: boolean,
  oppId: string
): Building[] {
  const buildings: Building[] = []
  // Add existing buildings to the front of the buildings array to maintain ordering.
  for (const offerBuilding of offerBuildings) {
    const buildingSfId = offerBuilding.details.id
    if (buildingSfId === 'sales_tool_created') {
      // The building was created locally, don't do anything.
      buildings.push(offerBuilding)
    } else {
      const opportunityBuilding = opportunityBuildings.find(b => sameBuilding(b, offerBuilding))
      if (opportunityBuilding != null) {
        // We found a matching CRM building.
        buildings.push(
          updateBuilding(
            offerBuilding,
            opportunityBuilding,
            isGroupedAssessment,
            defaultGroupsByKind,
            dxGrpId,
            isDoorOrVas,
            oppId
          )
        )
      } else if (offerBuilding.details.equipment.some(e => e.id === 'sales_tool_created')) {
        // There was no matching CRM building, but the building has some manually created equipment. In this case,
        // leave the building intact, but remove all CRM equipment from it.
        const equipment = offerBuilding.details.equipment.filter(e => e.id === 'sales_tool_created')
        const details = R.assoc('equipment', equipment, offerBuilding.details)
        buildings.push(R.assoc('details', details, offerBuilding))
      }
    }
  }
  // Add new buildings from CRM to the end of the buildings array.
  for (const opportunityBuilding of opportunityBuildings) {
    const offerBuilding = offerBuildings.find(b => sameBuilding(opportunityBuilding, b))
    if (offerBuilding == null) {
      // If we haven't found a matching building, it's a new building from CRM.
      buildings.push(
        createBuilding(opportunityBuilding, isGroupedAssessment, defaultGroupsByKind, dxGrpId, isDoorOrVas, oppId)
      )
    }
  }

  return buildings
}

function resetBuildingsAfterOpportunityCategoryChange(
  opportunityBuildings: OpportunityBuilding[],
  isGroupedAssessment: boolean,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  dxGrpId: string,
  isDoorOrVas: boolean,
  oppId: string
): Building[] {
  const buildings: Building[] = []
  for (const opportunityBuilding of opportunityBuildings) {
    buildings.push(
      createBuilding(opportunityBuilding, isGroupedAssessment, defaultGroupsByKind, dxGrpId, isDoorOrVas, oppId)
    )
  }
  return buildings
}

/*
  This function is used to see if the Building is the same even the Salesforce id has changed. In FSM, object type
  has changed as some old objects were replaced with new standard Salesforce objects. When user loads the new objects
  for the first time we can compare the address information as the Salesforce id has changed. This function can be
  simplified to ob.id === b.details.id after all user have used FSM version of KCC.
 */
// FIXME: Remove after FSM is taken use by all users
function sameBuilding(ob: OpportunityBuilding, b: Building) {
  if (ob.id === b.details.id) {
    return true
  }
  if (sameSfObjectType(ob.id, b.details.id)) {
    return false
  }
  return ob.buildingName &&
    ob.buildingName === b.details.buildingName &&
    ob.buildingAddress &&
    ob.buildingAddress === b.details.buildingAddress &&
    ob.buildingCity &&
    ob.buildingCity === b.details.buildingCity
    ? true
    : false
}

function createBuilding(
  opportunityBuilding: OpportunityBuilding,
  isGroupedAssessment: boolean,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  dxGrpId: string,
  isDoorOrVas: boolean,
  oppId: string
): Building {
  const salesToolId = getUUID()
  const equipment = opportunityBuilding.equipment.map(e =>
    createEquipment(e, salesToolId, isGroupedAssessment, defaultGroupsByKind, dxGrpId, isDoorOrVas, oppId)
  )

  return {
    details: {
      buildingAddress: nullify(opportunityBuilding.buildingAddress),
      buildingCity: nullify(opportunityBuilding.buildingCity),
      locationCountry: nullify(opportunityBuilding.buildingCountry),
      buildingDescription: nullify(opportunityBuilding.buildingDescription),
      buildingName: nullify(opportunityBuilding.buildingName),
      locationPostalCode: nullify(opportunityBuilding.buildingPostalCode),
      locationRegion: nullify(opportunityBuilding.buildingRegion),
      equipment,
      id: opportunityBuilding.id,
      salesToolId
    },
    salesToolId
  }
}

function updateBuilding(
  offerBuilding: Building,
  opportunityBuilding: OpportunityBuilding,
  isGroupedAssessment: boolean,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  dxGrpId: string,
  isDoorOrVas: boolean,
  oppId: string
): Building {
  const details: BuildingDetails = {
    ...offerBuilding.details,
    id: opportunityBuilding.id, // FIXME: Remove after FSM is taken use by all users. Due FSM change, Salesforce id is changed even we basically have the same data
    buildingAddress: nullify(opportunityBuilding.buildingAddress),
    buildingCity: nullify(opportunityBuilding.buildingCity),
    locationCountry: nullify(opportunityBuilding.buildingCountry),
    locationRegion: nullify(opportunityBuilding.buildingRegion),
    buildingDescription: nullify(opportunityBuilding.buildingDescription),
    buildingName: nullify(opportunityBuilding.buildingName),
    locationPostalCode: nullify(opportunityBuilding.buildingPostalCode),
    equipment: mergeEquipment(
      offerBuilding.details.equipment,
      opportunityBuilding.equipment,
      offerBuilding.salesToolId,
      isGroupedAssessment,
      defaultGroupsByKind,
      dxGrpId,
      isDoorOrVas,
      oppId
    )
  }

  return { ...offerBuilding, details }
}

function mergeEquipment(
  offerEquipment: Equipment[],
  opportunityEquipment: OpportunityEquipment[],
  buildingId: BuildingStId,
  isGroupedAssessment: boolean,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  dxGrpId: string,
  isDoorOrVas: boolean,
  oppId: string
): Equipment[] {
  const equipment: Equipment[] = []

  // Add existing equipment to the front of the equipment array to maintain ordering.
  for (const e of offerEquipment) {
    if (e.id === 'sales_tool_created') {
      equipment.push(e)
    } else {
      const oe = opportunityEquipment.find(item => sameEquipment(item, e))
      if (oe) {
        equipment.push(
          updateEquipment(oe, e, buildingId, isGroupedAssessment, defaultGroupsByKind, dxGrpId, isDoorOrVas, oppId)
        )
      }
    }
  }

  // Add new equipment from CRM to the end of the equipment array.
  for (const oe of opportunityEquipment) {
    const e = offerEquipment.find(item => sameEquipment(oe, item))
    if (!e && oe) {
      // If we haven't found a matching equipment, it's a new equipment from CRM.
      equipment.push(
        createEquipment(oe, buildingId, isGroupedAssessment, defaultGroupsByKind, dxGrpId, isDoorOrVas, oppId)
      )
    }
  }

  return equipment
}

/*
  This function is used to see if the Equipment is the same even the Salesforce id has changed. In FSM, object type
  has changed as some old objects were replaced with new standard Salesforce objects. When user loads the new objects
  for the first time we can compare the KEN as the Salesforce id has changed. This function can be simplified to
  oe.id === e.id after all user have used FSM version of KCC.
 */
// FIXME: Remove after FSM is taken use by all users
function sameEquipment(oe: OpportunityEquipment, e: Equipment) {
  return oe.id === e.id || (!sameSfObjectType(oe.id, e.id) && oe.koneEquipmentNumber === e.koneEquipmentNumber)
    ? true
    : false
}

/*
  This function is used to see if the object type has changed, which is the case when FSM was introduced and some
  old objects were replaced with new standard Salesforce objects. The first three letters defines the object type
  in Salesforce, which we use to check the equality.
 */
function sameSfObjectType(sfId: string, sfId2: string | null) {
  return sfId2 && sfId.substr(0, 3) === sfId2.substr(0, 3)
}

function getGroupForDx(oppId: string) {
  const getDxFromGlobal = DxGroup.get()
  const getDxGroupFromGlobal = getDxFromGlobal !== undefined ? getDxFromGlobal[oppId] : false
  if (getDxGroupFromGlobal) {
    return getDxGroupFromGlobal.salesToolId
  }
  const newGrp = createNewGrps(true, false, false)
  DxGroup.set({ [oppId]: newGrp })
  return newGrp.salesToolId
}

function createEquipment(
  opportunityEquipment: OpportunityEquipment,
  buildingId: BuildingStId,
  isGroupedAssessment: boolean,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  dxGrpId: string,
  isDoorOrVas: boolean,
  oppId: string
): Equipment {
  const equipmentBase = {
    buildingId,
    criticality: 'normal' as Criticality,
    description: nullify(opportunityEquipment.description),
    equipmentType: opportunityEquipment.equipmentType,
    id: opportunityEquipment.id,
    inventoryNumber: nullify(opportunityEquipment.inventoryNumber),
    koneEquipmentNumber: nullify(opportunityEquipment.koneEquipmentNumber),
    manufacturer: nullify(opportunityEquipment.manufacturer),
    manufacturingSerialnumber: nullify(opportunityEquipment.manufacturingSerialnumber),
    manufacturerPartNumber: nullify(opportunityEquipment.manufacturerPartNumber),
    operationalEnvironment: opportunityEquipment.operationalEnvironment,
    productOfferingId: null,
    salesToolId: getUUID(),
    autoEquipmentCreated: null,
    technicalPlatform: nullify(opportunityEquipment.technicalPlatform),
    year: nullify(opportunityEquipment.year),
    locationName: nullify(opportunityEquipment.locationName),
    locationAddress: nullify(opportunityEquipment.locationAddress),
    equipmentAddress: nullify(opportunityEquipment.equipmentAddress),
    locationCity: nullify(opportunityEquipment.locationCity),
    locationRegion: nullify(opportunityEquipment.locationRegion),
    locationCountry: nullify(opportunityEquipment.locationCountry),
    locationPostalCode: nullify(opportunityEquipment.locationPostalCode)
  }

  switch (opportunityEquipment.equipmentCategory) {
    case 'elevator':
      const grpId =
        !isDoorOrVas &&
        !R.isNil(nullify(opportunityEquipment.koneConnectivityDeviceType)) &&
        checkIfDx(opportunityEquipment)
          ? !R.isEmpty(dxGrpId)
            ? dxGrpId
            : getGroupForDx(oppId)
          : defaultGroupsByKind.elevators_and_escalators.salesToolId
      return {
        ...equipmentBase,
        groupId: isGroupedAssessment ? grpId : buildingId,
        additionalDoors: nullify(opportunityEquipment.additionalDoors),
        doorsCount: nullify(opportunityEquipment.doorsCount),
        equipmentCategory: 'elevator',
        ratedLoad: nullify(opportunityEquipment.ratedLoad),
        ratedSpeed: nullify(opportunityEquipment.ratedSpeed),
        controlSystem: nullify(opportunityEquipment.controlSystem),
        controllerType: nullify(opportunityEquipment.controllerType),
        technicalId: nullify(opportunityEquipment.technicalId),
        landingDoorType: nullify(opportunityEquipment.landingDoorType),
        carDoorType: nullify(opportunityEquipment.carDoorType),
        driveSystem: nullify(opportunityEquipment.driveSystem),
        apfComponent: nullify(opportunityEquipment.apfComponent),
        koneConnectivityDeviceType: nullify(opportunityEquipment.koneConnectivityDeviceType),
        equipmentOrigin: 'crm',
        isAPFFromCRM: opportunityEquipment.isAPFFromCRM,
        isKEDFromCRM: opportunityEquipment.isKEDFromCRM,
        startsPerYear: mapNumberOfStarts(opportunityEquipment.numberOfStarts || ''),
        locationDescription: ''
      }
    case 'escalator':
      return {
        ...equipmentBase,
        groupId: isGroupedAssessment ? defaultGroupsByKind.elevators_and_escalators.salesToolId : buildingId,
        equipmentCategory: 'escalator',
        escalatorType: nullify(opportunityEquipment.escalatorType),
        length: nullify(opportunityEquipment.length),
        numberOfDrives: nullify(opportunityEquipment.numberOfDrives),
        escalatorGearbox: nullify(opportunityEquipment.escalatorGearbox),
        operationMode: nullify(opportunityEquipment.operationMode),
        operationalEnvironmentCondition: nullify(opportunityEquipment.operationalEnvironmentCondition),
        rise: nullify(opportunityEquipment.rise),
        technicalId: nullify(opportunityEquipment.technicalId),
        balustradeMaterial: nullify(opportunityEquipment.balustradeMaterial),
        equipmentOrigin: 'crm',
        locationDescription: ''
      }
  }
}

function updateEquipment(
  opportunityEquipment: OpportunityEquipment,
  offerEquipment: Equipment,
  buildingId: BuildingStId,
  isGroupedAssessment: boolean,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  dxGrpId: string,
  isDoorOrVas: boolean,
  oppId: string
): Equipment {
  if (opportunityEquipment.equipmentCategory === 'elevator' && offerEquipment.equipmentCategory === 'elevator') {
    return updateElevator(opportunityEquipment, offerEquipment)
  } else if (
    opportunityEquipment.equipmentCategory === 'escalator' &&
    offerEquipment.equipmentCategory === 'escalator'
  ) {
    return updateEscalator(opportunityEquipment, offerEquipment)
  } else {
    // If equipment categories don't match, create a new equipment based on the CRM version
    return createEquipment(
      opportunityEquipment,
      buildingId,
      isGroupedAssessment,
      defaultGroupsByKind,
      dxGrpId,
      isDoorOrVas,
      oppId
    )
  }
}

function updateElevator(opportunityElevator: OpportunityElevator, offerElevator: Elevator): Elevator {
  return {
    ...offerElevator,
    id: opportunityElevator.id, // FIXME: Remove after FSM is taken use by all users. Due FSM change, Salesforce id is changed even we basically have the same data
    equipmentType: opportunityElevator.equipmentType,
    additionalDoors: nullify(opportunityElevator.additionalDoors),
    description: nullify(opportunityElevator.description),
    doorsCount: nullify(opportunityElevator.doorsCount),
    inventoryNumber: nullify(opportunityElevator.inventoryNumber),
    koneEquipmentNumber: nullify(opportunityElevator.koneEquipmentNumber),
    manufacturer: nullify(opportunityElevator.manufacturer),
    manufacturingSerialnumber: nullify(opportunityElevator.manufacturingSerialnumber),
    manufacturerPartNumber: nullify(opportunityElevator.manufacturerPartNumber),
    operationalEnvironment: opportunityElevator.operationalEnvironment,
    ratedLoad: nullify(opportunityElevator.ratedLoad),
    ratedSpeed: nullify(opportunityElevator.ratedSpeed),
    technicalPlatform: nullify(opportunityElevator.technicalPlatform),
    year: nullify(opportunityElevator.year),
    controlSystem: nullify(opportunityElevator.controlSystem),
    // controllerType is not yet in the CRM, It might be there in the future
    // for now retain value that is in the offer
    controllerType: nullify(opportunityElevator.controllerType) || offerElevator.controllerType,
    technicalId: nullify(opportunityElevator.technicalId),
    landingDoorType: nullify(opportunityElevator.landingDoorType),
    carDoorType: nullify(opportunityElevator.carDoorType),
    driveSystem: nullify(opportunityElevator.driveSystem),
    apfComponent: nullify(opportunityElevator.apfComponent),
    koneConnectivityDeviceType: nullify(opportunityElevator.koneConnectivityDeviceType),
    isAPFFromCRM: opportunityElevator.isAPFFromCRM,
    isKEDFromCRM: opportunityElevator.isKEDFromCRM,
    locationName: nullify(opportunityElevator.locationName),
    locationAddress: nullify(opportunityElevator.locationAddress),
    equipmentAddress: nullify(opportunityElevator.equipmentAddress),
    locationCity: nullify(opportunityElevator.locationCity),
    locationRegion: nullify(opportunityElevator.locationRegion),
    locationCountry: nullify(opportunityElevator.locationCountry),
    locationPostalCode: nullify(opportunityElevator.locationPostalCode),
    startsPerYear: mapNumberOfStarts(opportunityElevator.numberOfStarts || '')
  }
}

function updateEscalator(opportunityEscalator: OpportunityEscalator, offerEscalator: Escalator): Escalator {
  return {
    ...offerEscalator,
    id: opportunityEscalator.id, // FIXME: Remove after FSM is taken use by all users. Due FSM change, Salesforce id is changed even we basically have the same data
    description: nullify(opportunityEscalator.description),
    equipmentType: opportunityEscalator.equipmentType,
    escalatorType: nullify(opportunityEscalator.escalatorType),
    inventoryNumber: nullify(opportunityEscalator.inventoryNumber),
    koneEquipmentNumber: nullify(opportunityEscalator.koneEquipmentNumber),
    length: nullify(opportunityEscalator.length),
    manufacturer: nullify(opportunityEscalator.manufacturer),
    manufacturingSerialnumber: nullify(opportunityEscalator.manufacturingSerialnumber),
    manufacturerPartNumber: nullify(opportunityEscalator.manufacturerPartNumber),
    numberOfDrives: nullify(opportunityEscalator.numberOfDrives),
    escalatorGearbox: nullify(opportunityEscalator.escalatorGearbox),
    operationMode: nullify(opportunityEscalator.operationMode),
    operationalEnvironment: opportunityEscalator.operationalEnvironment,
    operationalEnvironmentCondition: nullify(opportunityEscalator.operationalEnvironmentCondition),
    rise: nullify(opportunityEscalator.rise),
    technicalPlatform: nullify(opportunityEscalator.technicalPlatform),
    year: nullify(opportunityEscalator.year),
    balustradeMaterial: nullify(opportunityEscalator.balustradeMaterial),
    technicalId: nullify(opportunityEscalator.technicalId),
    locationName: nullify(opportunityEscalator.locationName),
    locationAddress: nullify(opportunityEscalator.locationAddress),
    equipmentAddress: nullify(opportunityEscalator.equipmentAddress),
    locationCity: nullify(opportunityEscalator.locationCity),
    locationRegion: nullify(opportunityEscalator.locationRegion),
    locationCountry: nullify(opportunityEscalator.locationCountry),
    locationPostalCode: nullify(opportunityEscalator.locationPostalCode)
  }
}

function updateContacts(contacts: Contact[], opportunity: Opportunity, account: Customer): Contact[] {
  const opportunityContacts = opportunity.contacts
  const accountContacts = account.contacts
  const partnerContacts = (opportunity.partner && opportunity.partner.contacts) || []

  const newContacts: Contact[] = []
  opportunityContacts.forEach(oc => {
    const contactIndex: number = contacts.findIndex(
      c => c.contactRoleId === oc.contactRoleId || (c.id === oc.id && c.role === oc.role)
    )

    if (contactIndex === -1) {
      newContacts.push({ ...oc })
    }
  })
  contacts = contacts.concat(newContacts)

  const allContacts = accountContacts.concat(partnerContacts, opportunityContacts)
  return contacts.reduce((acc, contact) => {
    const matchingContact = allContacts.find(c => c.id === contact.id && c.isActiveContact)

    if (matchingContact) {
      const matchingOppContact = opportunityContacts.find(
        c => c.contactRoleId === contact.contactRoleId || (c.id === contact.id && c.role === contact.role)
      )
      if (matchingOppContact) {
        acc.push({
          ...matchingOppContact,
          primaryContact: contact.primaryContact
        })
      } else if (!contact.contactRoleId) {
        acc.push({
          ...contact
        })
      }
    }

    return acc
  }, [] as Contact[])
}

function updatePartnerFreeText(
  offerPartner?: Partner | null,
  opportunityPartner?: Partner | null,
  freeText?: string | null
) {
  const partnerWasRemoved = offerPartner && !opportunityPartner
  const partnerWasChanged =
    offerPartner && opportunityPartner && offerPartner.partnerAccountId !== opportunityPartner.partnerAccountId

  return partnerWasChanged || partnerWasRemoved ? undefined : freeText
}

function updateContractRoles(offer: Offer, opportunity: Opportunity): ContractRole[] {
  const { contractRoles, partner, partners } = offer
  const partnerWasAdded = partner == null && opportunity.partner != null
  const hasPartnerChanged = partners && opportunity.partners ? R.difference(partners, opportunity.partners) : null
  if (partnerWasAdded || hasPartnerChanged !== null) {
    return getContractPartnersRole(opportunity.partners)
  } else {
    // Assign every role that partner had to account.
    return contractRoles.map(({ id, role, type, accountNumber }) =>
      type === 'partner'
        ? { role, type: 'account' as const, accountNumber: '' }
        : { ...(id ? { id } : {}), role, type, accountNumber }
    )
  }
}

function updateAssessmentEquipmentGroupAnswers(
  assessmentEquipmentGroupAnswers: AssessmentEquipmentGroupAnswers,
  updatedBuildings: Building[]
) {
  const updatedBuildingIds = updatedBuildings.map(building => building.salesToolId)
  return R.pick(updatedBuildingIds, assessmentEquipmentGroupAnswers)
}

function updateEquipmentGroupsForGroupedAssessment(
  offer: Offer,
  updatedBuildings: ReadonlyArray<Building>,
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  isopportunityCategoryChanged: boolean
): EquipmentGroups {
  const isEquipmentInDefaultGroup =
    updatedBuildings.find(
      b =>
        b.details.equipment.find(e => {
          const groupKind = groupKindForCategory(e.equipmentCategory)
          const defaultGroup = defaultGroupsByKind[groupKind]
          return e.groupId === defaultGroup!.salesToolId
        }) !== undefined
    ) !== undefined
  const equipmentGroups = R.values(defaultGroupsByKind)
  const filteredEquipmentGroup = equipmentGroups.filter(
    group => group.groupKind === 'elevators_and_escalators' || group.groupKind === 'people_flow'
  )
  return !isopportunityCategoryChanged
    ? isEquipmentInDefaultGroup
      ? {
          ...offer.equipmentGroups,
          ...equipmentGroups.reduce((acc, eg) => ({ ...acc, [eg.salesToolId || '']: eg }), {})
        }
      : offer.equipmentGroups
    : { ...filteredEquipmentGroup.reduce((acc, eg) => ({ ...acc, [eg.salesToolId || '']: eg }), {}) }
}

function updateEquipmentGroupsForStandardAssessment(
  offer: Offer,
  updatedBuildings: ReadonlyArray<Building>
): EquipmentGroups {
  const { equipmentGroups: currentGroups } = offer
  return updatedBuildings.reduce((acc, building) => {
    const { salesToolId } = building

    if (currentGroups.hasOwnProperty(salesToolId)) {
      return R.assoc(salesToolId, currentGroups[salesToolId], acc)
    } else {
      const equipmentGroup = {
        name: building.details.buildingAddress || t('building.noAddress'),
        salesToolId
      }
      return R.assoc(salesToolId, equipmentGroup, acc)
    }
  }, {} as EquipmentGroups)
}

function updateEquipmentGroups(
  offer: Offer,
  updatedBuildings: Building[],
  defaultGroupsByKind: Record<GroupKind, EquipmentGroup>,
  isOpportunityCategoryChanged: boolean
): EquipmentGroups {
  return offer.assessmentMethod !== 'standard'
    ? updateEquipmentGroupsForGroupedAssessment(
        offer,
        updatedBuildings,
        defaultGroupsByKind,
        isOpportunityCategoryChanged
      )
    : updateEquipmentGroupsForStandardAssessment(offer, updatedBuildings)
}

function updateExtraServices(offer: Offer, updatedEquipmentGroups: EquipmentGroups): ExtraService[] {
  const currentExtraServices: ExtraService[] = offer.extraServices || ([] as ExtraService[])

  return R.map(currentExtraService => {
    R.map(answer => {
      answer.equipmentPrices = R.filter(
        equipmentPrice => R.has(equipmentPrice.equipmentGroup || '', updatedEquipmentGroups),
        answer.equipmentPrices
      )
      return answer
    }, currentExtraService.answers)
    return currentExtraService
  }, currentExtraServices)
}

function nullify<T>(obj: T | undefined): T | null {
  return obj != null ? obj : null
}

function mapNumberOfStarts(numberOfStarts: string): string {
  const starts = numberOfStarts ? parseInt(numberOfStarts, 10) : NaN
  if (Number.isInteger(starts)) {
    if (starts < 80000) {
      return '1'
    } else if (starts < 150000) {
      return '2'
    } else if (starts < 300000) {
      return '3'
    } else if (starts < 500000) {
      return '4'
    } else {
      return '5'
    }
  }
  return ''
}

export const createNewGrps = (isDx: boolean, isDoor: boolean, isVas: boolean = false) => ({
  name: isDx
    ? t('equipment.group.dxGroup.name')
    : isDoor || isVas
      ? t('equipment.group.default')
      : t('equipment.group.nonDxGroup.name'),
  salesToolId: getUUID(),
  groupKind: isDoor ? 'doors' : ('elevators_and_escalators' as GroupKind),
  isDx
})
